import { memo } from 'react';

export type ThemeId = 'neon-club' | 'rooftop' | 'cozy-lounge';

export const THEME_META: Record<
  ThemeId,
  { name: string; floor: string; sky: string; accent: string }
> = {
  'neon-club': { name: 'Neon club', floor: '#15121c', sky: '#0d0b14', accent: '#8b6fd6' },
  rooftop: { name: 'Rooftop', floor: '#2a2230', sky: '#3a2a3d', accent: '#f0a45b' },
  'cozy-lounge': { name: 'Cozy lounge', floor: '#2b1f1a', sky: '#1d1614', accent: '#e0a05a' },
};

/**
 * Full-bleed room backdrop, drawn behind the stage. Decorative only; slice-scaled so it fills
 * any aspect ratio. viewBox 1200x600, horizon at y≈330.
 */
export const Backdrop = memo(function Backdrop({
  theme,
  className,
}: {
  theme: ThemeId;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 1200 600"
      preserveAspectRatio="xMidYMid slice"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {theme === 'neon-club' && <NeonClub />}
      {theme === 'rooftop' && <Rooftop />}
      {theme === 'cozy-lounge' && <CozyLounge />}
    </svg>
  );
});

function NeonClub() {
  return (
    <>
      <defs>
        <linearGradient id="nc-wall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0c0a12" />
          <stop offset="1" stopColor="#1a1426" />
        </linearGradient>
        <linearGradient id="nc-floor" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1b1624" />
          <stop offset="1" stopColor="#0e0c13" />
        </linearGradient>
        <radialGradient id="nc-spot" cx="0.5" cy="0" r="0.8">
          <stop offset="0" stopColor="#8b6fd6" stopOpacity=".55" />
          <stop offset="1" stopColor="#8b6fd6" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="nc-spot2" cx="0.5" cy="0" r="0.8">
          <stop offset="0" stopColor="#4fb3c6" stopOpacity=".45" />
          <stop offset="1" stopColor="#4fb3c6" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="1200" height="600" fill="url(#nc-wall)" />
      {/* acoustic wall panels */}
      <g fill="#15111e" stroke="#211a2e" strokeWidth="2">
        {Array.from({ length: 12 }, (_, i) => (
          <rect key={i} x={i * 100 + 8} y="40" width="84" height="250" rx="6" />
        ))}
      </g>
      {/* neon strips */}
      <g className="cs-scene-glow">
        <rect x="60" y="300" width="1080" height="4" rx="2" fill="#8b6fd6" opacity=".8" />
        <rect x="60" y="22" width="1080" height="3" rx="1.5" fill="#4fb3c6" opacity=".6" />
      </g>
      <path d="M180 0 L60 330 L320 330 Z" fill="url(#nc-spot)" className="cs-scene-glow" />
      <path d="M1020 0 L880 330 L1140 330 Z" fill="url(#nc-spot2)" className="cs-scene-glow" />
      <path d="M600 0 L470 330 L730 330 Z" fill="url(#nc-spot)" opacity=".5" />
      <rect y="304" width="1200" height="296" fill="url(#nc-floor)" />
      {/* floor grid in perspective */}
      <g stroke="#2a2138" strokeWidth="1.5" opacity=".8">
        {Array.from({ length: 13 }, (_, i) => (
          <line key={i} x1={600 + (i - 6) * 40} y1="304" x2={600 + (i - 6) * 190} y2="600" />
        ))}
        {[330, 365, 410, 470, 545].map((y) => (
          <line key={y} x1="0" y1={y} x2="1200" y2={y} />
        ))}
      </g>
    </>
  );
}

function Rooftop() {
  return (
    <>
      <defs>
        <linearGradient id="rt-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2b2340" />
          <stop offset=".55" stopColor="#8a4a55" />
          <stop offset="1" stopColor="#e39a5c" />
        </linearGradient>
        <linearGradient id="rt-deck" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3b2f34" />
          <stop offset="1" stopColor="#1f1a1e" />
        </linearGradient>
      </defs>
      <rect width="1200" height="600" fill="url(#rt-sky)" />
      <circle cx="860" cy="300" r="70" fill="#f6c27a" opacity=".85" />
      <g className="cs-scene-drift" fill="#f3d9c0" opacity=".25">
        <ellipse cx="260" cy="90" rx="120" ry="14" />
        <ellipse cx="720" cy="140" rx="160" ry="12" />
        <ellipse cx="1040" cy="70" rx="90" ry="10" />
      </g>
      {/* skyline */}
      <g fill="#241c2a">
        <path d="M0 330 V250 H60 V210 H120 V270 H170 V180 H230 V260 H300 V230 H350 V290 H420 V200 H470 V160 H520 V260 H600 V240 H660 V290 H720 V210 H790 V250 H840 V190 H900 V270 H960 V230 H1030 V180 H1080 V260 H1140 V220 H1200 V330 Z" />
      </g>
      <g fill="#f2c178" opacity=".7">
        {[
          [70, 225],
          [90, 245],
          [185, 200],
          [205, 230],
          [435, 215],
          [490, 180],
          [495, 210],
          [735, 230],
          [855, 210],
          [865, 240],
          [1045, 200],
          [1060, 230],
          [1150, 240],
        ].map(([x, y]) => (
          <rect key={`${x}-${y}`} x={x} y={y} width="8" height="10" rx="1" />
        ))}
      </g>
      {/* railing */}
      <rect y="318" width="1200" height="6" fill="#5b4a4c" />
      <g stroke="#5b4a4c" strokeWidth="4">
        {Array.from({ length: 25 }, (_, i) => (
          <line key={i} x1={i * 50} y1="324" x2={i * 50} y2="352" />
        ))}
      </g>
      <rect y="352" width="1200" height="248" fill="url(#rt-deck)" />
      <g stroke="#2b2327" strokeWidth="2">
        {[380, 420, 470, 530, 600].map((y) => (
          <line key={y} x1="0" y1={y} x2="1200" y2={y} />
        ))}
      </g>
      {/* string lights */}
      <path
        d="M0 40 Q300 110 600 50 Q900 110 1200 40"
        stroke="#3a2e33"
        strokeWidth="2"
        fill="none"
      />
      <g fill="#ffd89a" className="cs-scene-glow">
        {Array.from({ length: 16 }, (_, i) => {
          const x = i * 80 + 20;
          const t = (x % 600) / 600;
          const y = 40 + Math.sin(t * Math.PI) * 45 + (x > 600 ? 0 : 0);
          return <circle key={i} cx={x} cy={y + 8} r="5" />;
        })}
      </g>
    </>
  );
}

function CozyLounge() {
  return (
    <>
      <defs>
        <linearGradient id="cl-wall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#231a17" />
          <stop offset="1" stopColor="#3a2821" />
        </linearGradient>
        <radialGradient id="cl-lamp" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#f4b867" stopOpacity=".55" />
          <stop offset="1" stopColor="#f4b867" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="1200" height="600" fill="url(#cl-wall)" />
      {/* wallpaper stripes */}
      <g fill="#2a1f1b">
        {Array.from({ length: 30 }, (_, i) => (
          <rect key={i} x={i * 40} y="0" width="18" height="320" />
        ))}
      </g>
      {/* bookshelves */}
      <g>
        <rect x="40" y="110" width="200" height="210" fill="#1c1411" />
        <rect x="960" y="110" width="200" height="210" fill="#1c1411" />
        {[140, 200, 260].map((y) => (
          <g key={y}>
            <rect x="40" y={y + 48} width="200" height="6" fill="#4a3428" />
            <rect x="960" y={y + 48} width="200" height="6" fill="#4a3428" />
            {Array.from({ length: 9 }, (_, i) => (
              <rect
                key={i}
                x={50 + i * 20}
                y={y + 8 + (i % 3) * 4}
                width="14"
                height={40 - (i % 3) * 4}
                fill={['#7a3b2e', '#44604f', '#8a6a3a', '#3f4a66'][i % 4]}
              />
            ))}
            {Array.from({ length: 9 }, (_, i) => (
              <rect
                key={`r${i}`}
                x={970 + i * 20}
                y={y + 8 + ((i + 1) % 3) * 4}
                width="14"
                height={40 - ((i + 1) % 3) * 4}
                fill={['#3f4a66', '#8a6a3a', '#7a3b2e', '#44604f'][i % 4]}
              />
            ))}
          </g>
        ))}
      </g>
      {/* lamps */}
      <circle cx="330" cy="180" r="160" fill="url(#cl-lamp)" className="cs-scene-glow" />
      <circle cx="870" cy="180" r="160" fill="url(#cl-lamp)" className="cs-scene-glow" />
      <path d="M305 150 L355 150 L345 120 L315 120 Z" fill="#d99a52" />
      <path d="M845 150 L895 150 L885 120 L855 120 Z" fill="#d99a52" />
      <rect x="328" y="150" width="4" height="170" fill="#4a3428" />
      <rect x="868" y="150" width="4" height="170" fill="#4a3428" />
      {/* floor + rug */}
      <rect y="320" width="1200" height="280" fill="#2b1f1a" />
      <g stroke="#34261f" strokeWidth="2">
        {Array.from({ length: 16 }, (_, i) => (
          <line key={i} x1={i * 80} y1="320" x2={i * 80 - 40} y2="600" />
        ))}
      </g>
      <ellipse cx="600" cy="470" rx="520" ry="110" fill="#5a2f2a" opacity=".85" />
      <ellipse
        cx="600"
        cy="470"
        rx="470"
        ry="92"
        fill="none"
        stroke="#c98a4a"
        strokeWidth="3"
        opacity=".6"
      />
    </>
  );
}

/** DJ booth front panel, rendered in front of the DJ avatar. viewBox 320x110. */
export const DjBooth = memo(function DjBooth({
  theme,
  className,
}: {
  theme: ThemeId;
  className?: string;
}) {
  const a = THEME_META[theme].accent;
  return (
    <svg viewBox="0 0 320 110" className={className} aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={`booth-${theme}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2c2a33" />
          <stop offset="1" stopColor="#17161b" />
        </linearGradient>
      </defs>
      <rect x="6" y="14" width="308" height="90" rx="10" fill={`url(#booth-${theme})`} />
      <rect x="0" y="6" width="320" height="16" rx="6" fill="#3a3842" />
      {/* decks */}
      <circle
        cx="70"
        cy="14"
        r="20"
        fill="#1d1c22"
        stroke="#4a4852"
        strokeWidth="2"
        transform="scale(1 .3) translate(0 30)"
      />
      <circle
        cx="250"
        cy="14"
        r="20"
        fill="#1d1c22"
        stroke="#4a4852"
        strokeWidth="2"
        transform="scale(1 .3) translate(0 30)"
      />
      <rect x="130" y="9" width="60" height="8" rx="2" fill="#24232a" />
      <rect
        x="20"
        y="52"
        width="280"
        height="3"
        rx="1.5"
        fill={a}
        opacity=".75"
        className="cs-scene-glow"
      />
      <g fill="#45434d">
        <rect x="40" y="68" width="60" height="22" rx="4" />
        <rect x="220" y="68" width="60" height="22" rx="4" />
      </g>
    </svg>
  );
});
